import React from 'react';
import type { ShippingAddress } from '../contexts/AuthContext';

interface CheckoutAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  addresses: ShippingAddress[];
  selectedId: number | null;
  onSelectId: (id: number) => void;
  cartTotal: number;
  shippingFee: number;
  onConfirm: () => void;
  isSubmitting: boolean;
  /** Optional VIP upsell for non-VIP users (billed separately after mock payment). */
  showVipUpsell?: boolean;
  vipPriceLine?: string;
  addVipSelected?: boolean;
  onAddVipSelectedChange?: (next: boolean) => void;
}

export default function CheckoutAddressModal({
  isOpen,
  onClose,
  addresses,
  selectedId,
  onSelectId,
  cartTotal,
  shippingFee,
  onConfirm,
  isSubmitting,
  showVipUpsell = false,
  vipPriceLine = '₹299/mo',
  addVipSelected = false,
  onAddVipSelectedChange,
}: CheckoutAddressModalProps) {
  if (!isOpen) return null;

  const grandTotal = cartTotal + shippingFee;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card text-card-foreground shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-1">Confirm checkout</h2>
        <p className="text-sm text-muted-foreground mb-4">Choose where we should ship your order.</p>

        <div className="space-y-2 mb-6">
          {addresses.map((a) => (
            <label
              key={a.id}
              className={`flex gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                selectedId === a.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50'
              }`}
            >
              <input
                type="radio"
                name="ship-addr"
                className="mt-1"
                checked={selectedId === a.id}
                onChange={() => onSelectId(a.id)}
              />
              <div className="text-sm">
                <p className="font-semibold">
                  {[a.first_name, a.last_name].filter(Boolean).join(' ')}
                  {a.is_default ? (
                    <span className="ml-2 inline-block text-xs font-normal text-primary">
                      (Default)
                    </span>
                  ) : null}
                </p>
                <p className="text-muted-foreground">{a.phone}</p>
                <p className="text-muted-foreground mt-1">
                  {a.address_line1}
                  {a.address_line2 ? `, ${a.address_line2}` : ''}
                </p>
                <p className="text-muted-foreground">
                  {a.district}, {a.state} — {a.pincode}
                </p>
              </div>
            </label>
          ))}
        </div>

        <div className="rounded-xl bg-muted/50 p-4 mb-6 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Items</span>
            <span>₹{cartTotal.toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Shipping</span>
            <span>₹{shippingFee.toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between font-bold text-base pt-2 border-t border-border">
            <span>Total</span>
            <span className="text-primary">₹{grandTotal.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {showVipUpsell && onAddVipSelectedChange ? (
          <label className="flex gap-3 items-start p-3 rounded-xl border border-border mb-6 cursor-pointer hover:bg-muted/40 transition-colors">
            <input
              type="checkbox"
              className="mt-1 rounded border-border"
              checked={addVipSelected}
              onChange={(e) => onAddVipSelectedChange(e.target.checked)}
            />
            <div className="text-sm">
              <p className="font-medium text-foreground">Add Jaalu VIP ({vipPriceLine})</p>
              <p className="text-muted-foreground text-xs mt-1">
                Adds monthly VIP to this order. You pay with your cart total; membership activates after checkout payment.
              </p>
            </div>
          </label>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-3 rounded-xl border border-border hover:bg-muted transition-colors disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || selectedId == null}
            className="px-4 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Placing order…' : 'Place order'}
          </button>
        </div>
      </div>
    </div>
  );
}
